// client/src/pages/admin/MissionReports.tsx
import React, { useState } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  DatePicker,
  Button,
  Table,
  Space,
  Typography,
  Progress,
  Tag,
  Spin
} from 'antd';
import {
  FileTextOutlined,
  TruckOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  DownloadOutlined,
  BarChartOutlined,
  UserOutlined,
  CarOutlined
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import { reportService } from '../../services/reportService';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const AdminMissionReports: React.FC = () => {
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>([
    dayjs().subtract(30, 'days'),
    dayjs()
  ]);

  // Fetch mission statistics
  const { data: missionReport, isLoading } = useQuery({
    queryKey: ['missionReport', dateRange],
    queryFn: () => {
      const start = dateRange?.[0]?.toISOString();
      const end = dateRange?.[1]?.toISOString();
      return reportService.getMissionStatistics(start, end);
    },
  });

  const handleExport = (format: 'pdf' | 'excel') => {
    // TODO: Implement export functionality
    console.log(`Exporting report as ${format}`);
  };

  const statistics = missionReport?.statistics;
  const monthlyTrend = missionReport?.monthly_trend || [];
  const missionTypes = missionReport?.by_mission_type || [];

  const missionTypesColumns: ColumnsType<any> = [
    {
      title: 'Type de mission',
      dataIndex: 'mission_type',
      key: 'mission_type',
    },
    {
      title: 'Total',
      dataIndex: 'total_missions',
      key: 'total_missions',
      render: (count: number) => <strong>{count}</strong>,
    },
    {
      title: 'Terminées',
      dataIndex: 'completed',
      key: 'completed',
      render: (completed: number) => (
        <Tag color="green">{completed}</Tag>
      ),
    },
    {
      title: 'Taux de réussite',
      dataIndex: 'completion_rate',
      key: 'completion_rate',
      render: (rate: number) => (
        <Progress
          percent={rate}
          size="small"
          strokeColor={rate >= 90 ? '#52c41a' : rate >= 75 ? '#fa8c16' : '#ff4d4f'}
        />
      ),
    },
    {
      title: 'Durée moy.',
      dataIndex: 'avg_duration_hours',
      key: 'avg_duration_hours',
      render: (hours: number) => hours ? `${hours.toFixed(1)}h` : '-',
    }
  ];

  if (isLoading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      {/* En-tête avec contrôles */}
      <Card style={{ marginBottom: '24px' }}>
        <Row align="middle" justify="space-between">
          <Col>
            <Title level={2} style={{ margin: 0 }}>
              <FileTextOutlined style={{ marginRight: '8px' }} />
              Rapport des missions
            </Title>
            <Text type="secondary">
              {dateRange?.[0]?.format('DD/MM/YYYY')} - {dateRange?.[1]?.format('DD/MM/YYYY')}
            </Text>
          </Col>
          <Col>
            <Space>
              <RangePicker
                value={dateRange}
                onChange={(dates) => setDateRange(dates)}
                format="DD/MM/YYYY"
              />
              <Button
                icon={<DownloadOutlined />}
                onClick={() => handleExport('pdf')}
              >
                PDF
              </Button>
              <Button
                icon={<DownloadOutlined />}
                onClick={() => handleExport('excel')}
              >
                Excel
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Métriques principales */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total missions"
              value={statistics?.total_missions || 0}
              prefix={<TruckOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
            <div style={{ marginTop: '8px' }}>
              <Text type="secondary">
                {statistics?.completed || 0} terminées
              </Text>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Taux de réussite"
              value={statistics?.completion_rate || 0}
              precision={1}
              suffix="%"
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
            <div style={{ marginTop: '8px' }}>
              <Progress
                percent={statistics?.completion_rate || 0}
                size="small"
                strokeColor="#52c41a"
              />
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Véhicules utilisés"
              value={statistics?.vehicles_used || 0}
              prefix={<CarOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
            <div style={{ marginTop: '8px' }}>
              <Text type="secondary">
                Dans la période
              </Text>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Conducteurs actifs"
              value={statistics?.drivers_active || 0}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#fa8c16' }}
            />
            <div style={{ marginTop: '8px' }}>
              <Text type="secondary">
                Dans la période
              </Text>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Indicateurs de statut */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={8}>
          <Card>
            <div style={{ textAlign: 'center', padding: '16px', background: '#f6ffed', borderRadius: '8px' }}>
              <CheckCircleOutlined style={{ fontSize: '32px', color: '#52c41a', marginBottom: '8px' }} />
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#52c41a' }}>
                {statistics?.completed || 0}
              </div>
              <div>Missions terminées</div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <div style={{ textAlign: 'center', padding: '16px', background: '#fff7e6', borderRadius: '8px' }}>
              <ClockCircleOutlined style={{ fontSize: '32px', color: '#fa8c16', marginBottom: '8px' }} />
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#fa8c16' }}>
                {(statistics?.in_progress || 0) + (statistics?.pending || 0)}
              </div>
              <div>En cours / En attente</div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <div style={{ textAlign: 'center', padding: '16px', background: '#fff2f0', borderRadius: '8px' }}>
              <ExclamationCircleOutlined style={{ fontSize: '32px', color: '#ff4d4f', marginBottom: '8px' }} />
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ff4d4f' }}>
                {statistics?.cancelled || 0}
              </div>
              <div>Missions annulées</div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Performance metrics */}
      {statistics?.avg_duration_hours && (
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col xs={24}>
            <Card>
              <Statistic
                title="Durée moyenne des missions"
                value={statistics.avg_duration_hours}
                precision={1}
                suffix="heures"
                prefix={<ClockCircleOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Répartition par type de mission */}
      {missionTypes.length > 0 && (
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col xs={24}>
            <Card
              title={
                <Space>
                  <BarChartOutlined />
                  Répartition par type de mission
                </Space>
              }
            >
              <Table
                columns={missionTypesColumns}
                dataSource={missionTypes}
                rowKey="mission_type"
                pagination={false}
                size="small"
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Évolution mensuelle */}
      {monthlyTrend.length > 0 && (
        <Row gutter={[16, 16]}>
          <Col xs={24}>
            <Card
              title={
                <Space>
                  <BarChartOutlined />
                  Évolution mensuelle (12 derniers mois)
                </Space>
              }
            >
              <Table
                columns={[
                  {
                    title: 'Mois',
                    dataIndex: 'month',
                    key: 'month',
                    render: (month: string) => dayjs(month).format('MMMM YYYY'),
                  },
                  {
                    title: 'Total',
                    dataIndex: 'total_missions',
                    key: 'total_missions',
                    render: (count: number) => <strong>{count}</strong>,
                  },
                  {
                    title: 'Terminées',
                    dataIndex: 'completed_missions',
                    key: 'completed_missions',
                    render: (count: number) => <Tag color="green">{count}</Tag>,
                  },
                  {
                    title: 'Annulées',
                    dataIndex: 'cancelled_missions',
                    key: 'cancelled_missions',
                    render: (count: number) => <Tag color="red">{count}</Tag>,
                  },
                  {
                    title: 'Taux de réussite',
                    dataIndex: 'completion_rate',
                    key: 'completion_rate',
                    render: (rate: number) => (
                      <Progress
                        percent={rate}
                        size="small"
                        strokeColor={rate >= 90 ? '#52c41a' : rate >= 75 ? '#fa8c16' : '#ff4d4f'}
                      />
                    ),
                  },
                ]}
                dataSource={monthlyTrend}
                rowKey="month"
                pagination={{ pageSize: 12 }}
                size="small"
              />
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default AdminMissionReports;